import { auth } from '../Services/Firebase/FirebaseIndex';
import { applyActionCode, checkActionCode } from 'firebase/auth';
import debug from './debug';

/**
 * Handle Firebase email action codes (verification, password reset, etc.)
 * This utility processes the action codes from Firebase email links
 */

export const handleEmailAction = async (actionCode, mode) => {
  debug.log('EmailActionHandler', 'Processing email action', { mode, actionCode: actionCode ? 'present' : 'missing' });

  try {
    if (!actionCode) {
      throw new Error('No action code provided');
    }

    // Check what the action code is for
    const info = await checkActionCode(auth, actionCode);
    debug.log('EmailActionHandler', 'Action code info', {
      operation: info.operation,
      email: info.data.email
    });

    switch (mode) {
      case 'verifyEmail':
        // Apply the email verification
        await applyActionCode(auth, actionCode);
        debug.log('EmailActionHandler', 'Email verification applied successfully');

        // Force reload the current user to update emailVerified status
        if (auth.currentUser) {
          await auth.currentUser.reload();
          debug.log('EmailActionHandler', 'User reloaded, emailVerified:', auth.currentUser.emailVerified);
        }

        return {
          success: true,
          operation: 'verifyEmail',
          email: info.data.email,
          message: 'Email verified successfully!'
        };

      case 'resetPassword':
        // For password reset, we don't apply the code here
        // Instead, we return the info so the component can handle it
        return {
          success: true,
          operation: 'resetPassword',
          email: info.data.email,
          actionCode: actionCode,
          message: 'Ready to reset password'
        };

      case 'recoverEmail':
        // Apply email recovery
        await applyActionCode(auth, actionCode);
        debug.log('EmailActionHandler', 'Email recovery applied successfully');

        return {
          success: true,
          operation: 'recoverEmail',
          email: info.data.email,
          message: 'Email recovered successfully!'
        };

      default:
        throw new Error(`Unknown action mode: ${mode}`);
    }

  } catch (error) {
    debug.error('EmailActionHandler', 'Error processing email action', error);

    // Map Firebase errors to user-friendly messages
    let errorMessage = 'Failed to process email action';

    switch (error.code) {
      case 'auth/expired-action-code':
        errorMessage = 'This link has expired. Please request a new one.';
        break;
      case 'auth/invalid-action-code':
        errorMessage = 'This link is invalid or has already been used.';
        break;
      case 'auth/user-disabled':
        errorMessage = 'This account has been disabled.';
        break;
      case 'auth/user-not-found':
        errorMessage = 'No account found for this email address.';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }

    return {
      success: false,
      error: errorMessage,
      code: error.code
    };
  }
};

/**
 * Parse URL parameters to extract Firebase action data
 */
export const parseEmailActionUrl = (url = window.location.href) => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    return {
      mode: params.get('mode'),
      actionCode: params.get('oobCode'),
      apiKey: params.get('apiKey'),
      lang: params.get('lang'),
      continueUrl: params.get('continueUrl')
    };
  } catch (error) {
    debug.error('EmailActionHandler', 'Error parsing URL', error);
    return {};
  }
};

/**
 * Check if the current URL contains a Firebase email action
 */
export const isFirebaseEmailAction = (url = window.location.href) => {
  const { mode, actionCode } = parseEmailActionUrl(url);
  return !!(mode && actionCode);
};